import React, { Component, createRef } from "react";
import styled from "styled-components";
import { withTenant } from "../../context/tenantConfig";
import Avatar from 'react-avatar';

const Section = styled.section`
  width: auto;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Salutation = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 6px;
  background: #f5f3ff;
  border: 1px solid #ede9fe;
  border-radius: 999px;
  padding: 6px 14px 6px 10px;

  span {
    font-size: 14px;
    text-transform: capitalize;
  }

  span:first-child {
    color: #9ca3af;
    font-weight: 400;
  }

  span:last-child {
    color: #4f46e5;
    font-weight: 600;
  }
`;

const Notification = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;

  span{
    position: absolute;
    top: 0px;
    right: 0px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #fb3636c2;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 10px;
    color: #fff;
  }
`

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FlexCenter = styled.div`
  display: flex;
  justify-content: center;
`;

const RelativeWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
`;

const AvatarWrapper = styled.div`
  position: relative;
  height: 42px;
  width: 42px;
  border-radius: 50%; 
  overflow: hidden;
`;

const ArrowIcon = styled.svg`
  padding-left: 10px;
  transition: transform 0.3s;
  transform: ${props => (props.open ? "scaleY(-1)" : "scaleY(1)")};
  fill: currentColor;
  color: #111827;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 40;
  width: 240px;
  padding: 6px;
  background-color: #fff;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.10), 0px 2px 6px rgba(0, 0, 0, 0.06);
  display: ${props => (props.open ? "block" : "none")};
`;

const UserInfo = styled.div`
  padding: 14px 14px 12px 14px;
  margin-bottom: 4px;
  background: linear-gradient(135deg, #f0f7ff 0%, #f5f0ff 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
  text-transform: uppercase;
`;

const UserInfoText = styled.div`
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Divider = styled.div`
  height: 1px;
  background: #f3f4f6;
  margin: 4px 6px;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  font-size: 13px;
  text-align: left;
  color: #374151;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background-color: #f5f3ff;
    color: #6d28d9;
  }
`;

const DropdownIcon = styled.span`
  font-size: 16px;
  display: flex;
  align-items: center;
  opacity: 0.7;
`;

const SignOutItem = styled(DropdownItem)`
  color: #6b7280;

  &:hover {
    background-color: #fff1f0;
    color: #dc2626;
  }
`;

class UserDropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dropdownOpen: false,
    };

    this.triggerRef = createRef();
    this.dropdownRef = createRef();
  }

  componentDidMount() {
    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  handleClickOutside = (e) => {
    if (
      !this.state.dropdownOpen ||
      this.dropdownRef.current.contains(e.target) ||
      this.triggerRef.current.contains(e.target)
    ) {
      return;
    }
    this.setState({ dropdownOpen: false });
  };

  handleKeyDown = (e) => {
    if (this.state.dropdownOpen && e.keyCode === 27) {
      this.setState({ dropdownOpen: false });
    }
  };

  toggleDropdown = () => {
    this.setState((prevState) => ({
      dropdownOpen: !prevState.dropdownOpen,
    }));
  };

  handleSignOut = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  render() {
    const { dropdownOpen } = this.state;
    const { tenant: { user } } = this.props;
  
    return (
      <Section>
        <Salutation>
          <span>Hi, </span>
          <span>{user.name}!</span>
        </Salutation>
        <Container>
          <FlexCenter>
            <RelativeWrapper>
              <Button onClick={this.toggleDropdown} innerRef={this.triggerRef}>
                <AvatarWrapper>
                  <Avatar name={user.name} size="40" textSizeRatio={0.9} round={true} />
                </AvatarWrapper>
                <ArrowIcon
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                  open={dropdownOpen}
                >
                  <path d="M10 14.25C9.8125 14.25 9.65625 14.1875 9.5 14.0625L2.3125 7C2.03125 6.71875 2.03125 6.28125 2.3125 6C2.59375 5.71875 3.03125 5.71875 3.3125 6L10 12.5312L16.6875 5.9375C16.9688 5.65625 17.4062 5.65625 17.6875 5.9375C17.9688 6.21875 17.9688 6.65625 17.6875 6.9375L10.5 14C10.3437 14.1562 10.1875 14.25 10 14.25Z" />
                </ArrowIcon>
              </Button>

              <DropdownMenu open={dropdownOpen} innerRef={this.dropdownRef}>
                <UserInfo>
                  <UserAvatar>
                    {user.name ? user.name.charAt(0) : 'U'}
                  </UserAvatar>
                  <UserInfoText>
                    <UserName>{user.name}</UserName>
                    <UserEmail>{user.email}</UserEmail>
                  </UserInfoText>
                </UserInfo>
                <DropdownItem onClick={() => {}}>
                  <DropdownIcon>👤</DropdownIcon>
                  Edit Profile
                </DropdownItem>
                <Divider />
                <SignOutItem onClick={this.handleSignOut}>
                  <DropdownIcon>🚪</DropdownIcon>
                  Sign Out
                </SignOutItem>
              </DropdownMenu>
            </RelativeWrapper>
          </FlexCenter>
        </Container>
      </Section>
    );
  }
}


export default withTenant(UserDropdown)